import React, { Component } from "react";
import _ from "lodash";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider, { CSVExport } from "react-bootstrap-table2-toolkit";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { debounce } from "throttle-debounce";
import { toast } from "react-toastify";
import RegistrationsService from "../../RegistrationsService";

/**
 * MN-2075: Attendance Report.
 *
 * Both find modes exist only to build one thing - a set of child_template ids. Everything
 * downstream (the report call, grouping by member, export) is keyed on that set and is
 * mode-agnostic, which is why "find by class" could be added without touching the endpoint.
 */

// MUST match ClassSearchResultLimit in Natca.Services/Services/RegistrationsService.cs. The server
// deliberately returns one extra row beyond this so the client can tell the list was truncated;
// that sentinel row is sliced off and never displayed. If you change one, change both.
const CLASS_SEARCH_DISPLAY_LIMIT = 50;

const NoDataIndication = () => (
  <div className="container h-100">
    <div className="row NoDataFoundTable">
      <h5>No Data Found!</h5>
    </div>
  </div>
);

const getExportFileName = (filetype) => {
  const formattedDate = new Date()
    .toLocaleDateString("en-US", {
      year: "2-digit",
      month: "2-digit",
      day: "2-digit",
    })
    .replace(/\//g, ".");
  return `Attendance Report ${formattedDate}.${filetype}`;
};

const MySearch = (props) => {
  let input;
  return (
    <form role="search" className="app-search px-2 my-2">
      <div className="form-group mb-0">
        <input
          ref={(n) => (input = n)}
          type="text"
          id="AttendanceReportSearch"
          className="form-control rounded border mb-1 py-2 w-100"
          placeholder="Member Search.."
          autoComplete="off"
          onChange={() => props.onSearch(input.value)}
        />
        <button
          type="button"
          className="c-pointer text-secondary bg-transparent border-0"
        >
          <i className="fa fa-search" />
        </button>
      </div>
    </form>
  );
};

const MyExportCSV = (props) => (
  <div>
    <i
      title="Export to csv"
      className="csvIconSize fas fa-file-csv pl-1 c-pointer"
      onClick={() => props.onExport()}
    ></i>
  </div>
);

const MyExportPDF = (props) => {
  const handleClick = () => {
    const { data } = props;
    if (_.isEmpty(data)) {
      return;
    }
    const tableBody = data.map((val) => [
      val.firstName,
      val.lastName,
      val.memberNumber,
      val.region,
      val.facility,
      val.email,
      `${val.attendedCount} of ${val.totalClasses}`,
      val.classesAttended,
    ]);
    const doc = new jsPDF();
    doc.autoTable({
      head: [
        [
          "FIRST NAME",
          "LAST NAME",
          "MEMBER NUMBER",
          "REGION",
          "FACILITY",
          "EMAIL",
          "CLASSES ATTENDED",
          "CLASSES",
        ],
      ],
      body: tableBody,
      tableWidth: "auto",
      styles: { cellPadding: 0.7, fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 20 },
        2: { cellWidth: 18 },
      },
    });
    doc.save(getExportFileName("pdf"));
  };

  return (
    <div>
      <i
        title="Export to pdf"
        className="csvIconSize fas fa-file-pdf pl-1 c-pointer"
        onClick={handleClick}
      >
        {" "}
      </i>
    </div>
  );
};

const columns = [
  { text: "FIRST NAME", dataField: "firstName", sort: true },
  { text: "LAST NAME", dataField: "lastName", sort: true },
  { text: "MEMBER NUMBER", dataField: "memberNumber", sort: true },
  { text: "REGION", dataField: "region", sort: true },
  { text: "FACILITY", dataField: "facility", sort: true },
  { text: "EMAIL", dataField: "email", sort: true },
  {
    // Numeric under the hood so sorting is by count, not by the "2 of 5" string.
    text: "CLASSES ATTENDED",
    dataField: "attendedCount",
    sort: true,
    formatter: (cell, row) => `${cell} of ${row.totalClasses}`,
    csvFormatter: (cell, row) => `${cell} of ${row.totalClasses}`,
  },
  { text: "CLASSES", dataField: "classesAttended", sort: false },
  // Always "Attended" in v1; kept so a future No-Show view is a filter change, not a rebuild.
  { text: "STATUS", dataField: "registrationStatus", sort: true },
];

export class AttendanceReport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      findBy: "course", // "course" | "class"
      courses: [],
      // Course mode: the courses whose classes are currently in the checklist, one chip each.
      // Held as objects, not ids - MUI needs them for `value` and `getOptionLabel`.
      selectedCourses: [],
      // Classes gathered from every added course, each carrying `selected`.
      classOptions: [],
      isLoadingClasses: false,
      classesError: null,
      // Names of added courses that returned no live classes. Not derivable - an empty
      // course leaves no trace in classOptions - and a list, so two empty courses both
      // get named in the message.
      coursesWithNoClasses: [],
      // Class mode: cross-course picks, rendered as removable chips.
      classSearchOptions: [],
      selectedClasses: [],
      isSearchingClasses: false,
      classSearchTruncated: false,
      // Results
      memberRows: [],
      isLoadingReport: false,
      reportError: null,
      hasRun: false,
      // The class count the run was made against, so "X of N" cannot drift when the
      // selection is edited after a run.
      ranAgainstClassCount: 0,
    };
    this.searchClassesDebounced = debounce(500, this.searchClasses);
  }

  async componentDidMount() {
    await this.loadCourses();
  }

  loadCourses = async () => {
    let data = await RegistrationsService.GetAdminTemplates();
    if (!_.isEmpty(data) && data.statusCode == 200) {
      this.setState({ courses: data.result != null ? data.result : [] });
    }
  };

  /** Any change to the selection invalidates the last run, so stale rows never linger. */
  resetResults = (nextState) => {
    this.setState({ ...nextState, memberRows: [], hasRun: false, reportError: null });
  };

  //#region Course mode

  /** Adds the picked course's classes to the checklist, all ticked, skipping duplicates. */
  addCourseClasses = async (course) => {
    if (course == null) {
      return;
    }
    this.setState({ isLoadingClasses: true, classesError: null });
    try {
      let data = await RegistrationsService.GetCourseClasses(course.id);
      let classes = !_.isEmpty(data) && data.result != null ? data.result : [];

      // Dedupe AND append inside the updater. Reading classOptions after the await and
      // spreading that snapshot let two quick adds clobber each other - the second write
      // was built from pre-fetch state, so the first course's classes silently vanished.
      // Computing existingIds outside would still mis-dedupe, so both live in here.
      this.setState((p) => {
        let existingIds = p.classOptions.map((c) => c.id);
        let added = classes
          .filter((c) => !existingIds.includes(c.id))
          .map((c) => ({ ...c, selected: true }));
        return {
          classOptions: [...p.classOptions, ...added],
          // Mirrors resetResults (inlined here because that helper takes an object, not an
          // updater) - a successful add changes the selection, so a stale run error must go.
          reportError: null,
          coursesWithNoClasses:
            classes.length === 0
              ? _.uniq([...p.coursesWithNoClasses, course.name])
              : p.coursesWithNoClasses,
          memberRows: [],
          hasRun: false,
        };
      });
    } catch (err) {
      // Roll the chip back. Committing selectedCourses before the fetch (so chips and the
      // checklist never disagree for a frame) means a failure would otherwise leave a chip for
      // a course whose classes are NOT in the checklist - and because classesError is cleared
      // by the next course change, the only evidence disappears on the next click. The report
      // then silently covers fewer courses than the chips claim.
      this.setState((p) => ({
        selectedCourses: p.selectedCourses.filter((c) => c.id !== course.id),
        classesError: `Could not load classes for "${course.name}". It was not added - please try again.`,
      }));
      toast.error(`Could not load classes for "${course.name}".`);
    } finally {
      // Object form on purpose: one independent flag, and it must reset even when the
      // updater above never queued.
      this.setState({ isLoadingClasses: false });
    }
  };

  /**
   * MUI `multiple` hands back the whole next array, never a delta, so the added/removed
   * courses are diffed against previous state here. One diff covers every removal gesture:
   * the chip's delete icon, re-clicking an already-selected option, and Backspace on an
   * empty input.
   *
   * Removing by masterTemplateId is total and unambiguous: child_template.template_id is a
   * single FK and GetCourseClasses filters on it, so a class can never come from two
   * courses. "Remove course X" is exactly "drop classes whose masterTemplateId is X".
   *
   * Deliberate semantics: unticked classes of a SURVIVING course keep their ticks; removing
   * a course drops all of its classes including ones the user deliberately kept (a kept
   * class with no chip would reintroduce the invisible-selection bug this fixes); re-adding
   * a course brings its classes back all ticked.
   */
  coursesChangeHandler = async (e, newValue) => {
    const next = newValue || [];
    const prevIds = this.state.selectedCourses.map((c) => c.id);
    const nextIds = next.map((c) => c.id);
    const added = next.filter((c) => !prevIds.includes(c.id));
    const removedIds = prevIds.filter((id) => !nextIds.includes(id));

    // One synchronous updater so chips and checklist never disagree for a frame. Inlines
    // the result reset rather than calling resetResults, which takes an object not an updater.
    this.setState((p) => ({
      selectedCourses: next,
      classOptions: p.classOptions.filter(
        (c) => !removedIds.includes(c.masterTemplateId)
      ),
      coursesWithNoClasses: p.coursesWithNoClasses.filter((n) =>
        next.some((c) => c.name === n)
      ),
      classesError: null,
      memberRows: [],
      hasRun: false,
      // Must mirror resetResults, which this updater deliberately inlines. Omitting it left a
      // stale "Could not run the report" alert on screen after the selection had changed.
      reportError: null,
    }));

    for (const course of added) {
      await this.addCourseClasses(course);
    }
  };

  toggleClass = (classId) => {
    this.resetResults({
      classOptions: this.state.classOptions.map((c) =>
        c.id === classId ? { ...c, selected: !c.selected } : c
      ),
    });
  };

  setAllClasses = (selected) => {
    this.resetResults({
      classOptions: this.state.classOptions.map((c) => ({ ...c, selected })),
    });
  };

  clearSelection = () => {
    this.resetResults({
      selectedCourses: [],
      classOptions: [],
      coursesWithNoClasses: [],
      classesError: null,
    });
  };

  //#endregion

  //#region Class mode

  searchClasses = async (term) => {
    this.setState({ isSearchingClasses: true });
    try {
      let data = await RegistrationsService.SearchClasses(term);
      let found = !_.isEmpty(data) && data.result != null ? data.result : [];
      // The server returns one MORE than the display limit purely as a truncation sentinel
      // (see ClassSearchResultLimit in RegistrationsService.cs). Never show that extra row.
      let truncated = found.length > CLASS_SEARCH_DISPLAY_LIMIT;
      this.setState({
        classSearchOptions: truncated
          ? found.slice(0, CLASS_SEARCH_DISPLAY_LIMIT)
          : found,
        classSearchTruncated: truncated,
      });
    } catch (err) {
      // Deliberately no red alert: this is a debounced typeahead, not a submitted action.
      // The loading indicator stopping plus a toast is the right weight.
      //
      // Clear the options rather than leaving the PREVIOUS term's results sitting under the
      // current text - they read as genuine matches for what was just typed. The truncation
      // hint has to go with them, or it claims ">50 match" for a search that in fact failed.
      this.setState({ classSearchOptions: [], classSearchTruncated: false });
      toast.error("Could not search classes.");
    } finally {
      this.setState({ isSearchingClasses: false });
    }
  };

  classesChangeHandler = (e, newValue) => {
    this.resetResults({ selectedClasses: newValue });
  };

  //#endregion

  switchFindBy = (findBy) => {
    // Each mode keeps its own selection; only the results are invalidated.
    this.resetResults({ findBy });
  };

  /** The report's only real input, whichever mode built it. */
  getSelectedClasses = () => {
    return this.state.findBy === "course"
      ? this.state.classOptions.filter((c) => c.selected)
      : this.state.selectedClasses;
  };

  /**
   * One row per (member, class attended) collapses to one row per member.
   * Distinct class ids guard against a member holding more than one attended
   * registration for the same class, which would otherwise inflate "X of N".
   */
  buildMemberRows = (rows, totalClasses) => {
    return _.map(_.groupBy(rows, "memberId"), (memberRows) => {
      let first = memberRows[0];
      let distinctClasses = _.uniqBy(memberRows, "childTemplateId");
      return {
        memberId: first.memberId,
        firstName: first.firstName,
        lastName: first.lastName,
        memberNumber: first.memberNumber,
        region: first.region,
        facility: first.facility,
        email: first.email,
        registrationStatus: first.registrationStatus,
        attendedCount: distinctClasses.length,
        totalClasses: totalClasses,
        classesAttended: distinctClasses.map((r) => r.className).join("; "),
      };
    });
  };

  runReport = async () => {
    let selected = this.getSelectedClasses();
    if (selected.length === 0) {
      return;
    }
    this.setState({ isLoadingReport: true, reportError: null });

    try {
      let data = await RegistrationsService.GetAttendanceReport(
        selected.map((c) => c.id)
      );
      let rows = !_.isEmpty(data) && data.result != null ? data.result : [];

      this.setState({
        memberRows: this.buildMemberRows(rows, selected.length),
        ranAgainstClassCount: selected.length,
        hasRun: true,
      });
    } catch (err) {
      this.setState({
        reportError:
          "Could not run the report. Please try again, or select fewer classes if you picked a large number.",
        memberRows: [],
        hasRun: false,
      });
      toast.error("Could not run the report.");
    } finally {
      // The reset that matters: without it a failed run left the button disabled on
      // "Running…" forever, with the global overlay still up.
      this.setState({ isLoadingReport: false });
    }
  };

  renderCourseMode() {
    let {
      courses,
      selectedCourses,
      classOptions,
      isLoadingClasses,
      classesError,
      coursesWithNoClasses,
    } = this.state;
    let selectedCount = this.getSelectedClasses().length;

    return (
      <>
        <div className="row">
          <div className="col-lg-6">
            <Autocomplete
              multiple
              id="reportCourse"
              options={courses}
              value={selectedCourses}
              // v4 compares by reference by default, and these options are refetched.
              getOptionSelected={(option, value) => option.id === value.id}
              getOptionLabel={(option) => (!_.isEmpty(option) ? option.name : "")}
              onChange={this.coursesChangeHandler}
              autoHighlight={true}
              ChipProps={{ classes: { root: "py-2 " } }}
              // No filterOptions override here on purpose: unlike the class picker (which is
              // server-filtered) the course list is fully client-side, so MUI's built-in
              // type-to-filter is what makes the box searchable. No filterSelectedOptions
              // either - leaving selected courses in the list keeps click-to-toggle as a
              // second way to remove one.
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Course"
                  variant="standard"
                  placeholder="Select one or more courses"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
            <small className="text-muted">
              Pick one or more courses. Each course adds its classes to the list
              below; remove a course's chip to remove its classes.
            </small>
          </div>
        </div>

        {isLoadingClasses && (
          <div className="mt-3 text-muted">Loading classes&hellip;</div>
        )}

        {classesError && (
          <div className="alert alert-danger mt-3" role="alert">
            {classesError}
          </div>
        )}

        {/* Outside the classOptions.length gate below on purpose: a course whose classes
            are all cancelled or deleted contributes nothing to that list, which is exactly
            why it used to render nothing at all. */}
        {coursesWithNoClasses.length > 0 && (
          <div className="alert alert-warning mt-3" role="alert">
            No live classes found for {coursesWithNoClasses.join(", ")}.
            Cancelled and deleted classes are not listed.
          </div>
        )}

        {classOptions.length > 0 && (
          <div className="mt-4">
            <div className="d-flex align-items-center mb-2">
              <h6 className="mb-0">
                Classes ({selectedCount} of {classOptions.length} selected)
              </h6>
              <button
                type="button"
                className="btn btn-sm btn-link ml-3"
                onClick={() => this.setAllClasses(true)}
              >
                Select all
              </button>
              <button
                type="button"
                className="btn btn-sm btn-link"
                onClick={() => this.setAllClasses(false)}
              >
                Clear all
              </button>
              <button
                type="button"
                className="btn btn-sm btn-link text-danger ml-auto"
                onClick={this.clearSelection}
              >
                Start over
              </button>
            </div>

            <div className="card">
              <div className="card-body py-2">
                {classOptions.map((c) => (
                  <div className="form-check py-1" key={c.id}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`class_${c.id}`}
                      checked={c.selected}
                      onChange={() => this.toggleClass(c.id)}
                    />
                    <label
                      className="form-check-label c-pointer"
                      htmlFor={`class_${c.id}`}
                    >
                      {c.name}
                      <small className="text-muted ml-2">
                        {c.courseName}
                        {c.dates ? ` · ${c.dates}` : ""}
                      </small>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  renderClassMode() {
    let {
      classSearchOptions,
      selectedClasses,
      isSearchingClasses,
      classSearchTruncated,
    } = this.state;

    return (
      <div className="row">
        <div className="col-lg-8">
          <Autocomplete
            multiple
            id="reportClasses"
            options={classSearchOptions}
            value={selectedClasses}
            filterSelectedOptions
            // The server already filters (class name OR course name, trimmed). MUI's default client-side
            // filter re-filters on the class name only, which silently discards every course-name match
            // and any term with surrounding spaces - so hand the server's list through untouched.
            // (filterSelectedOptions still hides already-picked classes; MUI applies it separately.)
            filterOptions={(options) => options}
            loading={isSearchingClasses}
            getOptionLabel={(option) =>
              typeof option === "object" ? option.name : ""
            }
            getOptionSelected={(option, value) => option.id === value.id}
            onChange={this.classesChangeHandler}
            onInputChange={(e, value) => this.searchClassesDebounced(value)}
            onOpen={() => {
              if (classSearchOptions.length === 0) {
                this.searchClasses("");
              }
            }}
            renderOption={(option) => (
              <div>
                <div>{option.name}</div>
                <small className="text-muted">
                  {option.courseName}
                  {option.dates ? ` · ${option.dates}` : ""}
                </small>
              </div>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Classes"
                variant="standard"
                placeholder="Search classes by name or course"
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
          <small className="text-muted">
            Search and pick classes directly - they can come from different
            courses.
          </small>
          {/* Guidance, not an error - the search worked, there are simply more matches than
              the picker shows. Without this a user whose class is the 51st match sees no
              reason why it is missing. */}
          {classSearchTruncated && (
            <div className="text-warning">
              <small>
                More than {CLASS_SEARCH_DISPLAY_LIMIT} classes match - type more
                to narrow the list.
              </small>
            </div>
          )}
        </div>
      </div>
    );
  }

  renderSelectionSummary() {
    let selected = this.getSelectedClasses();
    if (selected.length === 0) {
      return null;
    }
    let courseCount = _.uniq(selected.map((c) => c.masterTemplateId)).length;
    return (
      <small className="text-muted ml-2">
        {selected.length} class(es)
        {courseCount > 1 ? ` across ${courseCount} courses` : ""} selected.
      </small>
    );
  }

  renderResults() {
    let { memberRows, hasRun, ranAgainstClassCount } = this.state;

    if (!hasRun) {
      return null;
    }

    if (memberRows.length === 0) {
      return (
        <div className="alert alert-info mt-4" role="alert">
          <strong>No attendance found</strong> for the{" "}
          {ranAgainstClassCount} selected class(es).
          <br />
          {/* Deliberately does not mention the internal ticket that caused historic
              classes to go unmarked: it means nothing to an admin, it advertises a past
              defect at the moment they are already looking at an empty report, and it
              would still be here explaining ancient history long after it stopped being
              true. The timeless, actionable half is what remains. */}
          <small>
            Members appear here once they have been marked Attended on the
            class's attendance screen. Classes whose attendance was never
            recorded will stay empty.
          </small>
        </div>
      );
    }

    return (
      <div className="mt-4">
        <ToolkitProvider
          keyField="memberId"
          data={memberRows}
          columns={columns}
          exportCSV={{
            fileName: getExportFileName("csv"),
            exportAll: false,
            onlyExportFiltered: false,
          }}
          search
        >
          {(props) => (
            <div>
              <div className="d-flex justify-content-end">
                <MyExportCSV {...props.csvProps} />
                <MyExportPDF data={memberRows} />
              </div>

              <MySearch {...props.searchProps} />

              <BootstrapTable
                keyField="memberId"
                noDataIndication={() => <NoDataIndication />}
                {...props.baseProps}
              />
            </div>
          )}
        </ToolkitProvider>
      </div>
    );
  }

  render() {
    let { findBy, memberRows, hasRun, isLoadingReport, reportError } = this.state;
    let selected = this.getSelectedClasses();

    return (
      <div>
        <div className="btn-group mb-3" role="group" aria-label="Find by">
          <button
            type="button"
            className={
              "btn btn-sm " +
              (findBy === "course" ? "btn-primary" : "btn-outline-primary")
            }
            onClick={() => this.switchFindBy("course")}
          >
            Find by Course
          </button>
          <button
            type="button"
            className={
              "btn btn-sm " +
              (findBy === "class" ? "btn-primary" : "btn-outline-primary")
            }
            onClick={() => this.switchFindBy("class")}
          >
            Find by Class
          </button>
        </div>

        {findBy === "course" ? this.renderCourseMode() : this.renderClassMode()}

        <div className="mt-3">
          <button
            type="button"
            className="btn btn-sm btn-outline-success"
            disabled={selected.length === 0 || isLoadingReport}
            onClick={this.runReport}
          >
            {isLoadingReport ? "Running…" : "Run Report"}
          </button>
          {selected.length === 0 ? (
            <small className="text-muted ml-2">
              Select at least one class.
            </small>
          ) : (
            this.renderSelectionSummary()
          )}
        </div>

        {reportError && (
          <div className="alert alert-danger mt-3" role="alert">
            {reportError}
          </div>
        )}

        {hasRun && memberRows.length > 0 && (
          <h6 className="mt-4 mb-0">
            {memberRows.length} member(s) attended at least one of the{" "}
            {this.state.ranAgainstClassCount} selected class(es).
          </h6>
        )}

        {this.renderResults()}
      </div>
    );
  }
}

export default AttendanceReport;
